import React, { useState } from 'react';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Input, Modal, Typography } from 'antd';
import { EventControlApiKeysElement } from '@constants/event';
import { API_KEY_PREVIEW } from '@modules/APIKeys/constants/apiKeys';
import useApiKeys from '@modules/APIKeys/hooks/useApiKeys';
import styles from './apiKeyViewer.module.scss';

interface IApiKeyViewerProps {
  isOpen: boolean;
  onClose: () => void;
  unMaskedApiKey: string;
}

const { Paragraph, Text } = Typography;

const ApiKeyViewer: React.FC<IApiKeyViewerProps> = (props) => {
  const { isOpen, onClose, unMaskedApiKey } = props;

  const { trackApiKeyUserEvent } = useApiKeys();

  const [isCopied, setIsCopied] = useState(false);
  const [copyButtonIcon, setCopyButtonIcon] = useState(<CopyOutlined />);
  const [copyButtonText, setCopyButtonText] = useState(API_KEY_PREVIEW.COPY_BUTTON_TEXT);

  const copyApiKeyToClipboard = () => {
    navigator.clipboard.writeText(unMaskedApiKey);
    setCopyButtonText(API_KEY_PREVIEW.COPY_BUTTON_ALT_TEXT);
    setCopyButtonIcon(<CheckOutlined />);
    setIsCopied(true);
    trackApiKeyUserEvent({ event: EventControlApiKeysElement.COPY_KEY });
  };

  const onCancel = () => {
    if (!isCopied) {
      copyApiKeyToClipboard();
    }

    onClose();
  };

  return (
    <Modal
      title={API_KEY_PREVIEW.MODAL_TITLE}
      open={isOpen}
      footer={null}
      onCancel={onCancel}
      className={styles.modal}
    >
      <Alert
        banner
        message={<Text strong>{API_KEY_PREVIEW.INFO_TEXT}</Text>}
        type="info"
        showIcon
      />
      <div className={styles.modal_wrap}>
        <Flex vertical gap="small">
          <Paragraph>
            {API_KEY_PREVIEW.DESCRIPTION.map(
              (item: { content: string; bold?: boolean }, index: number) =>
                item.bold ? (
                  <Text key={index} strong>
                    {item.content}
                  </Text>
                ) : (
                  <Text key={index}>{item.content}</Text>
                ),
            )}
          </Paragraph>

          <Flex gap="small">
            <Input name="api-key" className={styles.modal_input} value={unMaskedApiKey} readOnly />
            <Button
              data-testid="copy-button"
              type="primary"
              onClick={copyApiKeyToClipboard}
              icon={copyButtonIcon}
            >
              {copyButtonText}
            </Button>
          </Flex>
        </Flex>
      </div>
    </Modal>
  );
};

export default ApiKeyViewer;
